import React, { useEffect, useRef, useState } from 'react';
import { IMPACT } from '../data/mock';
import { Reveal, useParallax } from './effects';

const CHEF_PORTRAIT =
  '/site/chef-portrait.webp';

const ImpactNumber = () => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const portraitRef = useParallax(0.05);

  useEffect(() => {
    if (started) return;
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStarted(true);
          const target = IMPACT.big;
          const duration = 2000;
          const startTime = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - startTime) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setCount(Math.round(target * eased));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [started]);

  return (
    <section
      ref={ref}
      id="stats"
      className="relative py-28 lg:py-44 bg-[#EFEBE3] overflow-hidden"
    >
      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, #1A1A1A 1px, transparent 0)',
          backgroundSize: '36px 36px',
        }}
      />

      <div className="relative container-x">
        {/* Editorial chapter label */}
        <Reveal y={16}>
          <div className="flex items-center gap-3 mb-14 lg:mb-20">
            <span className="h-px w-12 bg-[#9A7F4F]" />
            <span
              className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Bugünün Ölçeği
            </span>
          </div>
        </Reveal>

        {/* Magazine spread: portrait LEFT — caption/number/stats stacked RIGHT */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 lg:gap-x-16 gap-y-14 items-center">
          {/* CHEF PORTRAIT — left */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal y={28}>
              <div className="relative max-w-[480px] mx-auto lg:mx-0">
                {/* Editorial gold offset frame */}
                <div
                  className="absolute inset-0 translate-x-3 translate-y-3 lg:translate-x-4 lg:translate-y-4 border border-[#9A7F4F]/35 rounded-sm pointer-events-none"
                  aria-hidden
                />
                <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-[0_40px_90px_-30px_rgba(114,39,45,0.50)]">
                  <img
                    ref={portraitRef}
                    src={CHEF_PORTRAIT}
                    alt="Tarhun Catering şefi"
                    className="absolute inset-0 w-full h-full object-cover will-change-transform"
                    style={{ transform: 'scale(1.08)' }}
                  />
                  {/* Bottom gradient anchor */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C0B]/85 via-[#0A0C0B]/15 to-transparent" />

                  {/* Editorial caption overlay */}
                  <div className="absolute bottom-7 left-7 right-7">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="h-px w-8 bg-[#C9A875]" />
                      <span
                        className="text-[10px] tracking-[0.32em] uppercase text-[#C9A875] font-medium"
                        style={{ fontFamily: '"JetBrains Mono", monospace' }}
                      >
                        Tarhun Şefi
                      </span>
                    </div>
                    <p
                      className="font-display italic text-[#F5F0E5] text-[22px] lg:text-[26px] leading-[1.22] tracking-[-0.005em]"
                      style={{
                        fontWeight: 300,
                        fontVariationSettings: '"opsz" 36, "SOFT" 30',
                      }}
                    >
                      Her tabakta aynı disiplin.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — caption → giant number → stats */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            {/* Italic setup caption */}
            <Reveal delay={150}>
              <p
                className="font-display italic text-[#1A1A1A] leading-[1.32] tracking-[-0.005em] max-w-[460px]"
                style={{
                  fontSize: 'clamp(20px, 1.85vw, 26px)',
                  fontVariationSettings: '"opsz" 36, "SOFT" 80',
                  fontWeight: 300,
                }}
              >
                her gün, dakikası dakikasına servis edilen öğün
              </p>
            </Reveal>

            {/* GIANT NUMBER */}
            <div
              className="font-display numeric text-[#9A7F4F] leading-[0.86] tracking-[-0.045em] font-medium flex items-start mt-6 lg:mt-8"
              style={{
                fontSize: 'clamp(108px, 14vw, 220px)',
                fontVariationSettings: '"opsz" 144, "SOFT" 30',
                opacity: started ? 1 : 0,
                transition: 'opacity 800ms cubic-bezier(0.22,1,0.36,1)',
                whiteSpace: 'nowrap',
              }}
            >
              <span>{count.toLocaleString('tr-TR')}</span>
              <span
                className="font-light"
                style={{
                  fontSize: '0.34em',
                  marginLeft: '0.06em',
                  marginTop: '0.18em',
                  display: 'inline-block',
                  letterSpacing: 0,
                }}
              >
                +
              </span>
            </div>

            {/* Sub-line under number */}
            <Reveal delay={400} y={14}>
              <div className="mt-4 lg:mt-5 flex items-center gap-3">
                <span
                  className="text-[10.5px] numeric tracking-[0.30em] text-[#1A1A1A]/55 font-medium"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  Günlük Servis
                </span>
                <span className="h-px flex-1 max-w-[80px] bg-[#9A7F4F]/40" />
              </div>
            </Reveal>

            {/* Soft gold divider */}
            <Reveal delay={500}>
              <div className="mt-12 lg:mt-14 mb-9 h-px w-24 bg-[#9A7F4F]/55" />
            </Reveal>

            {/* Sub-stats — horizontal rhythm */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-7">
              {IMPACT.subStats.map((s, i) => (
                <Reveal key={i} delay={600 + i * 110} y={14}>
                  <div className="border-t border-[#9A7F4F]/30 pt-4">
                    <div
                      className="font-display numeric font-medium text-[#1A1A1A] leading-none tracking-[-0.012em]"
                      style={{
                        fontSize: 'clamp(22px, 1.8vw, 28px)',
                        fontVariationSettings: '"opsz" 96, "SOFT" 30',
                      }}
                    >
                      {s.v}
                    </div>
                    <div
                      className="mt-3 text-[10px] tracking-[0.30em] uppercase text-[#1A1A1A]/68 font-medium leading-[1.5]"
                      style={{ fontFamily: '"JetBrains Mono", monospace' }}
                    >
                      {s.l}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactNumber;
